/**
 * Storage keys and public URLs live here so the local disk backend and S3 can
 * never drift apart.
 *
 * Note the deliberate split between the two namespaces:
 *   `/media/...`  is served from the media bucket (tiles, thumbs, json)
 *   `/photos/...` is an app route served by the SPA
 * Keeping them apart means `/photos/<id>/<slug>` can never collide with a tile
 * path in the CloudFront behaviour list.
 */

/** Keys under `originals/` are private: no CloudFront behaviour points at them. */
export function originalKey(id: string, filename: string): string {
  const ext = extensionOf(filename)
  return `originals/${id}${ext}`
}

export function idFromOriginalKey(key: string): string | null {
  const match = /^originals\/([^/.]+)(?:\.[^/]+)?$/.exec(key)
  return match ? match[1] : null
}

export function mediaPrefix(id: string): string {
  return `media/${id}/`
}

export function infoKey(id: string): string {
  return `media/${id}/info.json`
}

export function thumbKey(id: string): string {
  return `media/${id}/thumb.jpg`
}

export function tileKey(id: string, z: number, x: number, y: number): string {
  return `media/${id}/tiles/${z}/${x}_${y}.jpg`
}

export const INDEX_KEY = 'media/index.json'

/** One JSON per album, holding its photo list. */
export function albumKey(id: string): string {
  return `media/albums/${id}.json`
}

export const ALBUMS_PREFIX = 'media/albums/'

/** Storage keys map 1:1 onto CDN paths — `media/a/b.jpg` is served at `/media/a/b.jpg`. */
export function publicUrl(key: string): string {
  return `/${key}`
}

export function tileUrlTemplate(id: string): string {
  return `/media/${id}/tiles/{z}/{x}_{y}.jpg`
}

/** The canonical page for a photo. */
export function photoPath(id: string, slug: string): string {
  return `/photos/${id}/${slug}`
}

/** The canonical page for an album. */
export function albumPath(id: string, slug: string): string {
  return `/albums/${id}/${slug}`
}

function extensionOf(filename: string): string {
  const match = /\.([a-zA-Z0-9]{1,8})$/.exec(filename)
  return match ? `.${match[1].toLowerCase()}` : '.jpg'
}
